import { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import InputField from "@/components/form/components/InputField";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, PageContainer } from "@/components";
import { ProfileType, getProfilePhotoUrl } from "@/services/profile.service";
import {
  useUpdateProfileMutation,
  useUpdateProfilePhotoMutation
} from "@/hooks/api/profile.queries";
import {
  Linkedin,
  Github,
  User,
  Camera,
  Mail,
  Phone,
  ChevronLeft,
  Briefcase,
  MapPin,
  Sparkles,
  Save
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useState, useRef } from "react";
import { Badge } from "@/components/ui/badge";

const formSchema = z.object({
  bio: z.string().max(500).optional(),
  linkedin_url: z.string().url().or(z.literal("")).optional(),
  github_url: z.string().url().or(z.literal("")).optional(),
  portfolio_url: z.string().url().or(z.literal("")).optional(),
  preferred_locations: z.array(z.string()).optional(),
  preferred_domains: z.array(z.string()).optional(),
  is_open_to_work: z.boolean().optional(),
  personal_email: z.string().email().or(z.literal("")).optional(),
  phone: z.string().min(10, "Phone number too short").max(15).or(z.literal("")).optional(),
  full_name: z.string().min(2, "Name too short").max(100).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProfileFormProps {
  initialData: ProfileType;
  onCancel: () => void;
}

const ProfileForm = ({ initialData, onCancel }: ProfileFormProps) => {
  const updateProfileMutation = useUpdateProfileMutation();
  const updatePhotoMutation = useUpdateProfilePhotoMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tagInput, setTagInput] = useState({ loc: "", dom: "" });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bio: initialData.bio ?? "",
      linkedin_url: initialData.linkedin_url ?? "",
      github_url: initialData.github_url ?? "",
      portfolio_url: initialData.portfolio_url ?? "",
      preferred_locations: initialData.preferred_locations ?? [],
      preferred_domains: initialData.preferred_domains ?? [],
      is_open_to_work: initialData.is_open_to_work ?? true,
      personal_email: initialData.personal_email ?? "",
      phone: initialData.phone ?? "",
      full_name: initialData.full_name ?? "",
    },
  });

  // Explicitly watch locations and domains for immediate re-rendering
  const watchedLocations = useWatch({ control: form.control, name: "preferred_locations" }) || [];
  const watchedDomains = useWatch({ control: form.control, name: "preferred_domains" }) || [];

  const onSubmit = (values: FormValues) => {
    updateProfileMutation.mutate(values, {
      onSuccess: () => onCancel(),
    });
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      updatePhotoMutation.mutate(file);
    }
  };

  const addTag = (type: 'loc' | 'dom') => {
    const val = tagInput[type].trim();
    if (!val) return;

    const field = type === 'loc' ? 'preferred_locations' : 'preferred_domains';
    const current = form.getValues(field) || [];
    if (!current.includes(val)) {
      form.setValue(field, [...current, val], {
        shouldValidate: true,
        shouldDirty: true
      });
    }
    setTagInput(prev => ({ ...prev, [type]: "" }));
  };

  const removeTag = (type: 'loc' | 'dom', tag: string) => {
    const field = type === 'loc' ? 'preferred_locations' : 'preferred_domains';
    const current = form.getValues(field) || [];
    form.setValue(field, current.filter(t => t !== tag), {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  return (
    <PageContainer className="pb-12 space-y-8">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="gap-2 rounded-xl text-slate-500">
          <ChevronLeft size={20} /> Back to Profile
        </Button>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Editor Mode</span>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* ─── Left Column: Identity & Photo ─── */}
            <div className="space-y-6">
              <Card className="rounded-[2.5rem] border-none shadow-xl shadow-slate-200/50 overflow-hidden bg-white">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-widest text-indigo-600">Identity</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-6 p-8">
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-3xl overflow-hidden border-4 border-slate-100 bg-slate-50 group-hover:border-indigo-100 transition-all duration-300">
                      {initialData.profile_photo_url ? (
                        <img
                          src={getProfilePhotoUrl(initialData.profile_photo_url, 'md') || ""}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <User size={48} />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-2 -right-2 p-2 bg-indigo-600 text-white rounded-xl shadow-lg hover:bg-indigo-700 transition-all"
                      disabled={updatePhotoMutation.isPending}
                    >
                      <Camera size={18} />
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                  <div className="text-center space-y-4 w-full">
                    <InputField
                      type="text"
                      name="full_name"
                      label="Full Name"
                      className={{
                        labelClass: "text-[10px] font-black uppercase tracking-widest text-slate-400 text-center", // Added text-center because it was wrapped in a div with text-center
                        inputClass: "h-11 rounded-xl bg-slate-50/50 border-none focus-visible:ring-indigo-600 text-center font-black text-xl"
                      }}
                    />
                    <p className="text-xs text-slate-400 font-bold">{initialData.roll_number}</p>
                  </div>

                  <div className="w-full pt-6 border-t border-slate-50 space-y-4">
                    <FormField
                      control={form.control as any}
                      name="personal_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400">Personal Email</FormLabel>
                          <FormControl>
                            <div className="relative group">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-indigo-600" />
                              <Input {...field} className="pl-10 h-11 rounded-xl bg-slate-50/50 border-none focus-visible:ring-indigo-600" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control as any}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400">Phone Number</FormLabel>
                          <FormControl>
                            <div className="relative group">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-indigo-600" />
                              <Input {...field} className="pl-10 h-11 rounded-xl bg-slate-50/50 border-none focus-visible:ring-indigo-600" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ─── Right Column: Bio, Socials & Preferences ─── */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-[2.5rem] border-none shadow-xl shadow-slate-200/50 overflow-hidden bg-white">
                <CardHeader className="bg-slate-50/50 border-b border-white py-6 px-8 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-black text-slate-900">Portfolio Details</CardTitle>
                    <p className="text-slate-400 text-xs mt-1">Refine your professional narrative and preferences.</p>
                  </div>
                  <Button
                    type="submit"
                    className="rounded-2xl bg-indigo-600 hover:bg-indigo-700 font-bold px-8 shadow-lg shadow-indigo-100"
                    disabled={updateProfileMutation.isPending}
                  >
                    {updateProfileMutation.isPending ? "Saving..." : <><Save className="mr-2 h-4 w-4" /> Save Profile</>}
                  </Button>
                </CardHeader>
                <CardContent className="p-8 space-y-8">
                  {/* Bio */}
                  <FormField
                    control={form.control as any}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles size={16} className="text-amber-500" />
                          <FormLabel className="text-sm font-bold text-slate-700">Professional Bio</FormLabel>
                        </div>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="I am a passionate software engineer specializing in..."
                            className="min-h-[120px] rounded-2xl bg-slate-50/50 border-none focus-visible:ring-indigo-600 resize-none p-4"
                          />
                        </FormControl>
                        <FormDescription className="text-right text-[10px]">Max 500 characters</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Social Links */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control as any}
                      name="linkedin_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold text-slate-500">LinkedIn Profile</FormLabel>
                          <FormControl>
                            <div className="relative group">
                              <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-600" />
                              <Input {...field} placeholder="https://linkedin.com/in/..." className="pl-10 h-11 rounded-xl bg-slate-50/50 border-none focus-visible:ring-indigo-600" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control as any}
                      name="github_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold text-slate-500">GitHub Profile</FormLabel>
                          <FormControl>
                            <div className="relative group">
                              <Github className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-slate-900" />
                              <Input {...field} placeholder="https://github.com/..." className="pl-10 h-11 rounded-xl bg-slate-50/50 border-none focus-visible:ring-indigo-600" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Preferences Section */}
                  <div className="space-y-6 pt-6 border-t border-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Domain Preferences */}
                      <div className="space-y-3">
                        <FormLabel className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          <Briefcase size={16} className="text-indigo-600" /> Preferred Domains
                        </FormLabel>
                        <div className="flex gap-2">
                          <Input
                            value={tagInput.dom}
                            onChange={(e) => setTagInput(prev => ({ ...prev, dom: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('dom'))}
                            placeholder="e.g. Backend, UI/UX"
                            className="h-10 rounded-xl bg-slate-50/50 border-none"
                          />
                          <Button type="button" onClick={() => addTag('dom')} size="icon" className="h-10 w-10 shrink-0 bg-slate-900 rounded-xl">
                            <span className="text-white">+</span>
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {watchedDomains.map(tag => (
                            <Badge variant="secondary" key={tag} className="bg-indigo-50 text-indigo-700 border-none hover:bg-red-50 hover:text-red-700 transition-all cursor-pointer group" onClick={() => removeTag('dom', tag)}>
                              {tag} <span className="ml-1 opacity-0 group-hover:opacity-100">×</span>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Location Preferences */}
                      <div className="space-y-3">
                        <FormLabel className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          <MapPin size={16} className="text-violet-600" /> Preferred Locations
                        </FormLabel>
                        <div className="flex gap-2">
                          <Input
                            value={tagInput.loc}
                            onChange={(e) => setTagInput(prev => ({ ...prev, loc: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('loc'))}
                            placeholder="e.g. Remote, Bangalore"
                            className="h-10 rounded-xl bg-slate-50/50 border-none"
                          />
                          <Button type="button" onClick={() => addTag('loc')} size="icon" className="h-10 w-10 shrink-0 bg-slate-900 rounded-xl">
                            <span className="text-white">+</span>
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {watchedLocations.map(tag => (
                            <Badge variant="secondary" key={tag} className="bg-violet-50 text-violet-700 border-none hover:bg-red-50 hover:text-red-700 transition-all cursor-pointer group" onClick={() => removeTag('loc', tag)}>
                              {tag} <span className="ml-1 opacity-0 group-hover:opacity-100">×</span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-6 rounded-3xl bg-slate-50/50 border border-slate-100 shadow-inner">
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-slate-900">Open to Work</h4>
                        <p className="text-[10px] text-slate-400">Showcase your intent to recruiters.</p>
                      </div>
                      <FormField
                        control={form.control as any}
                        name="is_open_to_work"
                        render={({ field }) => (
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="data-[state=checked]:bg-indigo-600"
                            />
                          </FormControl>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Form>
    </PageContainer>
  );
};

export default ProfileForm;
